import { useEffect, useState, useCallback } from 'react'
import {
  X, Building2, Calendar, Layers, Ruler, Home, Search, Loader2, Zap,
  Flame, Thermometer, MapPin, Users, Landmark, SquareStack, Shield,
} from 'lucide-react'
import { cn, anneeToEpoque } from '@/lib/utils'
import useExpertiseStore from '@/stores/useExpertiseStore'
import { getParcelleData } from '@/api/data'


// --- DPE Badge coloré ---

const DPE_COLORS = {
  A: 'bg-green-600 text-white',
  B: 'bg-green-400 text-white',
  C: 'bg-yellow-300 text-slate-900',
  D: 'bg-yellow-400 text-slate-900',
  E: 'bg-orange-400 text-white',
  F: 'bg-orange-600 text-white',
  G: 'bg-red-600 text-white',
}

function DpeBadge({ classe }) {
  if (!classe) return null
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-xs font-bold', DPE_COLORS[classe] || 'bg-slate-200 text-slate-700')}>
      DPE {classe}
    </span>
  )
}

function GesBadge({ classe }) {
  if (!classe) return null
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-xs font-bold', DPE_COLORS[classe] || 'bg-slate-200 text-slate-700')}>
      GES {classe}
    </span>
  )
}


// --- Ligne info avec icône ---

function InfoRow({ icon: Icon, label, value, className }) {
  if (value == null || value === '' || value === 'INDETERMINE') return null
  return (
    <div className={cn('flex items-start gap-1.5 text-sm text-slate-600', className)}>
      <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
      <span>
        <span className="text-slate-500">{label} </span>
        <span className="text-slate-800">{value}</span>
      </span>
    </div>
  )
}


// --- Section pliable dans la carte ---

function CardSection({ title, children }) {
  const hasContent = Array.isArray(children)
    ? children.some(Boolean)
    : Boolean(children)
  if (!hasContent) return null
  return (
    <div className="mt-3 pt-3 border-t border-slate-100">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{title}</p>
      {children}
    </div>
  )
}


// --- Carte bâtiment enrichie ---

function BatimentCard({ batiment, selected, onSelect }) {
  const bat = batiment.batiment || {}
  const ffo = batiment.ffo_bat || {}
  const bdtopo = batiment.bdtopo_bat || {}
  const adresse = batiment.adresse || {}
  const dpe = batiment.dpe_logement
  const proprietaires = batiment.proprietaires || []
  const parcelles = batiment.parcelles || []

  return (
    <div
      onClick={onSelect}
      className={cn(
        'rounded-xl border p-4 cursor-pointer transition-all',
        selected
          ? 'border-carex-500 ring-2 ring-carex-200 bg-carex-50'
          : 'border-slate-200 hover:border-slate-300 hover:shadow-sm bg-white'
      )}
    >
      {/* En-tête : adresse + commune */}
      <div className="mb-2">
        {adresse.libelle_adr_principale_ban && (
          <p className="text-sm font-medium text-slate-900 leading-tight">
            {adresse.libelle_adr_principale_ban}
          </p>
        )}
        {bat.libelle_commune_insee && (
          <p className="text-xs text-slate-500">{bat.libelle_commune_insee}</p>
        )}
      </div>

      {/* Caractéristiques principales */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <InfoRow icon={Calendar} label="Construction" value={ffo.annee_construction} />
        <InfoRow icon={Layers} label="Niveaux" value={ffo.nb_niveau != null ? `${ffo.nb_niveau}` : null} />
        <InfoRow icon={Home} label="Usage" value={ffo.usage_niveau_1_txt} />
        <InfoRow icon={Ruler} label="Hauteur" value={bdtopo.hauteur_mean != null ? `${Math.round(bdtopo.hauteur_mean)} m` : null} />
        <InfoRow icon={Building2} label="Logements" value={ffo.nb_log > 0 ? ffo.nb_log : null} />
        <InfoRow icon={SquareStack} label="Emprise" value={bat.s_geom_groupe ? `${bat.s_geom_groupe} m²` : null} />
      </div>

      {/* Matériaux */}
      {(ffo.mat_mur_txt || ffo.mat_toit_txt) && (
        <div className="mt-2 flex flex-wrap gap-1">
          {ffo.mat_mur_txt && ffo.mat_mur_txt !== 'INDETERMINE' && (
            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
              Murs : {ffo.mat_mur_txt.toLowerCase()}
            </span>
          )}
          {ffo.mat_toit_txt && ffo.mat_toit_txt !== 'INDETERMINE' && (
            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
              Toit : {ffo.mat_toit_txt.toLowerCase()}
            </span>
          )}
        </div>
      )}

      {/* DPE détaillé */}
      {dpe && (
        <CardSection title="Diagnostic énergétique">
          <div className="flex flex-wrap gap-1.5 mb-2">
            <DpeBadge classe={dpe.classe_bilan_dpe} />
            <GesBadge classe={dpe.classe_emission_ges} />
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {dpe.conso_5_usages_ep_m2 != null && (
              <InfoRow icon={Flame} label="Conso" value={`${Math.round(dpe.conso_5_usages_ep_m2)} kWh/m²/an`} />
            )}
            {dpe.emission_ges_5_usages_m2 != null && (
              <InfoRow icon={Thermometer} label="GES" value={`${Math.round(dpe.emission_ges_5_usages_m2)} kgCO₂/m²/an`} />
            )}
            <InfoRow icon={Flame} label="Chauffage" value={dpe.type_energie_chauffage} />
            <InfoRow icon={Flame} label="ECS" value={dpe.type_energie_ecs} />
            {dpe.surface_habitable_logement != null && (
              <InfoRow icon={Home} label="Surface" value={`${dpe.surface_habitable_logement} m²`} />
            )}
            {dpe.surface_habitable_immeuble != null && (
              <InfoRow icon={Building2} label="Immeuble" value={`${dpe.surface_habitable_immeuble} m²`} />
            )}
            <InfoRow icon={Shield} label="Isolation murs" value={dpe.type_isolation_mur_exterieur !== 'Non isole' ? dpe.type_isolation_mur_exterieur : null} />
            <InfoRow icon={Shield} label="Vitrage" value={dpe.type_vitrage} />
            {dpe.periode_construction_dpe && (
              <InfoRow icon={Calendar} label="Période" value={dpe.periode_construction_dpe} />
            )}
            {dpe.date_etablissement_dpe && (
              <InfoRow icon={Calendar} label="Date DPE" value={new Date(dpe.date_etablissement_dpe).toLocaleDateString('fr-FR')} />
            )}
          </div>
        </CardSection>
      )}

      {/* Propriétaires */}
      {proprietaires.length > 0 && (
        <CardSection title="Propriétaires">
          <div className="space-y-1">
            {proprietaires.map((prop, i) => (
              <div key={prop.personne_id || i} className="flex items-start gap-1.5 text-sm">
                <Users className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                <div>
                  <span className="text-slate-800 font-medium">{prop.denomination || 'Propriétaire inconnu'}</span>
                  {prop.forme_juridique && (
                    <span className="text-xs text-slate-500 ml-1">({prop.forme_juridique})</span>
                  )}
                  {prop.siren && (
                    <span className="text-xs text-slate-400 ml-1">SIREN {prop.siren}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardSection>
      )}

      {/* Parcelles rattachées */}
      {parcelles.length > 1 && (
        <CardSection title="Parcelles rattachées">
          <div className="flex flex-wrap gap-1">
            {parcelles.map((p) => (
              <span
                key={p.parcelle_id}
                className={cn(
                  'text-xs px-2 py-0.5 rounded',
                  p.parcelle_principale
                    ? 'bg-carex-50 text-carex-700 font-medium'
                    : 'bg-slate-100 text-slate-600'
                )}
              >
                <MapPin className="w-3 h-3 inline mr-0.5" />
                {p.parcelle_id}
                {p.parcelle_principale && ' (principale)'}
              </span>
            ))}
          </div>
        </CardSection>
      )}

      {/* IRIS / EPCI */}
      {(bat.code_iris || bat.code_epci_insee) && (
        <CardSection title="Zonage">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <InfoRow icon={Landmark} label="IRIS" value={bat.code_iris} />
            <InfoRow icon={Landmark} label="EPCI" value={bat.code_epci_insee} />
          </div>
        </CardSection>
      )}

      {selected && (
        <div className="mt-3 pt-2 border-t border-carex-200 text-xs text-carex-600 font-medium">
          Bâtiment sélectionné
        </div>
      )}
    </div>
  )
}


// --- Panel principal ---

export default function ParcelleSelectionPanel({ selectedParcelles = [], onRemoveParcelle }) {
  const { updateField } = useExpertiseStore()
  const [batimentsMap, setBatimentsMap] = useState({})
  const [selectedBatimentKey, setSelectedBatimentKey] = useState(null)

  // Fetch BDNB data pour chaque parcelle sélectionnée
  useEffect(() => {
    for (const parcelle of selectedParcelles) {
      if (batimentsMap[parcelle.id]) continue

      setBatimentsMap((prev) => ({
        ...prev,
        [parcelle.id]: { loading: true, error: null, batiments: [] },
      }))

      getParcelleData(parcelle.id)
        .then((data) => {
          const batiments = data?.bdnb?.batiments || []
          setBatimentsMap((prev) => ({
            ...prev,
            [parcelle.id]: { loading: false, error: null, batiments },
          }))
        })
        .catch(() => {
          setBatimentsMap((prev) => ({
            ...prev,
            [parcelle.id]: { loading: false, error: 'Erreur de chargement', batiments: [] },
          }))
        })
    }
  }, [selectedParcelles]) // eslint-disable-line react-hooks/exhaustive-deps

  // Nettoyer les parcelles retirées
  useEffect(() => {
    const ids = new Set(selectedParcelles.map((p) => p.id))
    setBatimentsMap((prev) => {
      const next = {}
      for (const [k, v] of Object.entries(prev)) {
        if (ids.has(k)) next[k] = v
      }
      return next
    })
  }, [selectedParcelles])

  const handleSelectBatiment = useCallback(
    (parcelleId, index, batiment) => {
      const key = `${parcelleId}:${index}`
      setSelectedBatimentKey(key)

      const ffo = batiment.ffo_bat || {}
      const bdtopo = batiment.bdtopo_bat || {}
      const dpe = batiment.dpe_logement

      // --- Auto-fill bâti ---
      if (ffo.annee_construction) {
        updateField('parcelles.0.biens.0.bati.date_construction_libre', String(ffo.annee_construction))
        const epoque = anneeToEpoque(ffo.annee_construction)
        if (epoque) {
          updateField('parcelles.0.biens.0.bati.epoque_construction', epoque)
        }
      }
      if (ffo.nb_niveau != null) {
        updateField('parcelles.0.biens.0.bati.nombre_etages', ffo.nb_niveau)
      }
      if (ffo.mat_mur_txt && ffo.mat_mur_txt !== 'INDETERMINE') {
        updateField('parcelles.0.biens.0.bati.gros_oeuvre', ffo.mat_mur_txt)
      }
      if (ffo.mat_toit_txt && ffo.mat_toit_txt !== 'INDETERMINE') {
        updateField('parcelles.0.biens.0.bati.toiture_couverture', ffo.mat_toit_txt)
      }
      if (bdtopo.hauteur_mean != null) {
        updateField('parcelles.0.biens.0.bati.hauteur_moyenne', bdtopo.hauteur_mean)
      }

      // --- Type de bien déduit de l'usage FFO ---
      if (ffo.usage_niveau_1_txt) {
        const usageMapping = {
          'Résidentiel individuel': 'maison',
          'Résidentiel collectif': 'appartement',
        }
        const typeBien = usageMapping[ffo.usage_niveau_1_txt]
        if (typeBien) {
          updateField('parcelles.0.biens.0.type_bien', typeBien)
        }
      }

      // --- Auto-fill DPE ---
      if (dpe) {
        if (dpe.classe_bilan_dpe) {
          updateField('parcelles.0.biens.0.performance_energetique.dpe_disponible', true)
          updateField('parcelles.0.biens.0.performance_energetique.dpe_impact', `Classe ${dpe.classe_bilan_dpe}`)
        }
        if (dpe.classe_emission_ges) {
          updateField('parcelles.0.biens.0.performance_energetique.ges_classe', dpe.classe_emission_ges)
        }
        if (dpe.conso_5_usages_ep_m2 != null) {
          updateField('parcelles.0.biens.0.performance_energetique.conso_energie', Math.round(dpe.conso_5_usages_ep_m2))
        }
        if (dpe.emission_ges_5_usages_m2 != null) {
          updateField('parcelles.0.biens.0.performance_energetique.emission_ges', Math.round(dpe.emission_ges_5_usages_m2))
        }
        if (dpe.date_etablissement_dpe) {
          updateField('parcelles.0.biens.0.performance_energetique.dpe_date', dpe.date_etablissement_dpe)
        }
        if (dpe.identifiant_dpe) {
          updateField('parcelles.0.biens.0.performance_energetique.dpe_numero', dpe.identifiant_dpe)
        }
        if (dpe.type_energie_chauffage) {
          updateField('parcelles.0.biens.0.equipements.chauffage_alimentation', dpe.type_energie_chauffage)
        }
        if (dpe.type_vitrage) {
          updateField('parcelles.0.biens.0.bati.huisseries_vitrage', dpe.type_vitrage)
        }
        if (dpe.surface_habitable_logement != null) {
          updateField('parcelles.0.biens.0.surfaces.habitable_m2', dpe.surface_habitable_logement)
        }
      }

      // Stocker les données brutes BDNB complètes
      updateField('parcelles.0.situation.bdnb_data', { batiments: [batiment] })
      updateField('parcelles.0.situation.bdnb_selected_index', index)
    },
    [updateField]
  )

  const totalBatiments = Object.values(batimentsMap).reduce(
    (sum, entry) => sum + (entry.batiments?.length || 0),
    0
  )
  const anyLoading = Object.values(batimentsMap).some((entry) => entry.loading)

  if (selectedParcelles.length === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-3">
        <Search className="w-4 h-4" />
        Cliquez sur une parcelle sur la carte pour la sélectionner
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Parcelles sélectionnées */}
      <div>
        <h4 className="text-sm font-medium text-slate-700 mb-2">
          Parcelles sélectionnées ({selectedParcelles.length})
        </h4>
        <div className="flex flex-wrap gap-2">
          {selectedParcelles.map((p) => (
            <span
              key={p.id}
              className="inline-flex items-center gap-1.5 bg-bronze-50 text-bronze-700 border border-bronze-200 rounded-full px-3 py-1 text-sm font-medium"
            >
              Section {p.section} n°{p.numero}
              <span className="text-xs text-bronze-500">
                ({p.contenance?.toLocaleString('fr-FR')} m²)
              </span>
              <button
                type="button"
                onClick={() => onRemoveParcelle(p.id)}
                className="ml-0.5 text-bronze-400 hover:text-bronze-700 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Bâtiments BDNB */}
      <div>
        <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
          <Building2 className="w-4 h-4" />
          Bâtiments identifiés
          {anyLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          {!anyLoading && totalBatiments > 0 && (
            <span className="text-xs text-slate-500 font-normal">({totalBatiments})</span>
          )}
        </h4>

        {!anyLoading && totalBatiments === 0 && (
          <p className="text-sm text-slate-500 py-2">
            Aucun bâtiment référencé dans la BDNB pour les parcelles sélectionnées.
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {selectedParcelles.map((parcelle) => {
            const entry = batimentsMap[parcelle.id]
            if (!entry) return null

            if (entry.loading) {
              return (
                <div key={parcelle.id} className="rounded-xl border border-slate-200 p-4 flex items-center gap-2 text-sm text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Chargement des bâtiments pour {parcelle.section} n°{parcelle.numero}...
                </div>
              )
            }

            if (entry.error) {
              return (
                <div key={parcelle.id} className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">
                  {entry.error} — Section {parcelle.section} n°{parcelle.numero}
                </div>
              )
            }

            return entry.batiments.map((bat, idx) => (
              <BatimentCard
                key={`${parcelle.id}:${idx}`}
                batiment={bat}
                selected={selectedBatimentKey === `${parcelle.id}:${idx}`}
                onSelect={() => handleSelectBatiment(parcelle.id, idx, bat)}
              />
            ))
          })}
        </div>

        {selectedBatimentKey && (
          <p className="text-xs text-carex-600 mt-2 flex items-center gap-1">
            <Zap className="w-3.5 h-3.5" />
            Les données du bâtiment sélectionné ont été reportées dans les onglets Description et Urbanisme.
          </p>
        )}
      </div>
    </div>
  )
}
